import React from 'react';
import Header from '../components/common/Header';
import Footer from '../components/Footer/Footer';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

// Terms and Conditions — static, no data/API calls. Same visual system
// as ContactUs.jsx / PrivacyPolicy.jsx: gradient hero, #EEF3FA page
// background, white cards with soft shadows.
//
// Placeholders to replace before launch: LAST_UPDATED and (in the text)
// the company's legal name / governing-law country.
// Have a lawyer review this text before you publish it.

const LAST_UPDATED = 'October 17, 2026';

const C = {
  blue: '#0F4C75',
  blueDeep: '#0B3A5B',
  orange: '#DF8525',
  ink: '#1C2B36',
  body: '#546374',
  muted: '#7C8A9A',
  bg: '#EEF3FA',
  card: '#FFFFFF',
  line: '#E1E9F4',
  tint: '#0F4C750D',
  noteBg: '#FFF4E4',
  noteBorder: '#F3D9AE',
  noteText: '#6B4A12',
};

const s = {
  page: { background: C.bg, paddingBottom: 72 },
  hero: {
    background: `linear-gradient(135deg, ${C.blueDeep} 0%, ${C.blue} 55%, #16628f 100%)`,
    padding: '72px 16px 88px',
    textAlign: 'center',
    color: '#fff',
  },
  eyebrow: {
    display: 'inline-block', padding: '6px 16px', borderRadius: 999,
    background: 'rgba(255,255,255,0.14)', fontSize: 12.5, fontWeight: 700,
    letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 18,
  },
  h1: { margin: '0 0 12px', fontSize: 'clamp(30px, 5vw, 44px)', fontWeight: 800, lineHeight: 1.15 },
  sub: { margin: '0 auto', maxWidth: 620, fontSize: 16.5, lineHeight: 1.7, color: 'rgba(255,255,255,0.88)' },
  updated: { marginTop: 14, fontSize: 13.5, color: 'rgba(255,255,255,0.75)' },
  breadcrumb: { marginTop: 10, fontSize: 13.5, color: 'rgba(255,255,255,0.75)' },
  breadcrumbLink: { color: '#fff', textDecoration: 'none', fontWeight: 600 },
  wrap: { maxWidth: '90%', margin: '-56px auto 0', padding: '0 16px' },
  toc: {
    background: C.card, borderRadius: 18, boxShadow: '0 20px 48px -24px rgba(15, 76, 117, 0.25)',
    border: `1px solid ${C.line}`, padding: '26px 32px', marginBottom: 22,
  },
  tocTitle: { margin: '0 0 14px', fontSize: 13, fontWeight: 700, color: C.orange, letterSpacing: 0.6, textTransform: 'uppercase' },
  tocList: { margin: 0, padding: 0, listStyle: 'none', columnWidth: 240, columnGap: 24 },
  tocItem: { margin: '0 0 8px', fontSize: 14, lineHeight: 1.5, breakInside: 'avoid' },
  tocLink: { color: C.body, textDecoration: 'none', fontWeight: 600 },
  notice: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    background: C.noteBg, border: `1px solid ${C.noteBorder}`, borderRadius: 12,
    padding: '14px 18px', fontSize: 14, lineHeight: 1.6, color: C.noteText, marginBottom: 22,
  },
  card: {
    background: C.card, borderRadius: 18, boxShadow: '0 20px 48px -24px rgba(15, 76, 117, 0.25)',
    border: `1px solid ${C.line}`, padding: '30px 32px', marginBottom: 20,
  },
  sectionHead: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 },
  badge: {
    width: 34, height: 34, borderRadius: 10, background: C.tint, color: C.blue,
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800,
    fontSize: 14, flexShrink: 0,
  },
  h2: { margin: 0, fontSize: 19, fontWeight: 800, color: C.ink },
  p: { margin: '0 0 12px', fontSize: 15, lineHeight: 1.75, color: C.body },
  ul: { margin: '0 0 4px', paddingLeft: 20 },
  li: { margin: '0 0 8px', fontSize: 14.5, lineHeight: 1.65, color: C.body },
  lead: { color: C.ink, fontWeight: 700 },
  inlineLink: { color: C.blue, fontWeight: 600 },
  footer: { margin: '28px 0 0', fontSize: 13, color: C.muted, textAlign: 'center' },
};

// Bold lead-in used for the sub-topics inside the subscriptions section.
const Lead = ({ children }) => <strong style={s.lead}>{children}</strong>;

function Section({ id, index, heading, paragraphs = [], bullets = [], after = [] }) {
  return (
    <div id={id} style={s.card}>
      <div style={s.sectionHead}>
        <span style={s.badge}>{index}</span>
        <h2 style={s.h2}>{heading}</h2>
      </div>
      {paragraphs.map((t, i) => <p key={`p${i}`} style={s.p}>{t}</p>)}
      {bullets.length > 0 && (
        <ul style={s.ul}>
          {bullets.map((t, i) => <li key={`b${i}`} style={s.li}>{t}</li>)}
        </ul>
      )}
      {after.map((t, i) => <p key={`a${i}`} style={{ ...s.p, marginBottom: i === after.length - 1 ? 0 : 12 }}>{t}</p>)}
    </div>
  );
}

const SECTIONS = [
  {
    id: 'acceptance',
    heading: 'Acceptance of these terms',
    paragraphs: [
      'These Terms and Conditions ("Terms") are an agreement between you and Future Family ("we", "us", "our"). By creating an account or using our website or app you confirm that you have read, understood and agree to them, and to our Privacy Policy.',
      'If you do not agree, please do not use Future Family.',
    ],
  },
  {
    id: 'medical',
    heading: 'Medical disclaimer',
    paragraphs: [
      'Future Family provides health tracking, educational information and access to independent healthcare professionals. The content in the app, including scores, trends, alerts and reports, is for information only and is not a diagnosis or a substitute for professional medical advice.',
      'Always talk to a qualified doctor about your health, fertility or family-planning decisions. Never ignore medical advice or delay seeking it because of something you saw in the app.',
    ],
    after: [
      'Future Family is not for emergencies. If you think you are having a medical emergency, call your local emergency number immediately.',
    ],
  },
  {
    id: 'eligibility',
    heading: 'Eligibility',
    paragraphs: [
      'You must be at least 18 years old and able to enter a binding agreement to use Future Family.',
    ],
  },
  {
    id: 'account',
    heading: 'Your account',
    paragraphs: [
      'You are responsible for the information you give us and for keeping it accurate, including your email, phone number and country. Your country is used to show appointment times in the right time zone.',
    ],
    bullets: [
      'Keep your password confidential and do not share your account.',
      'You are responsible for everything that happens under your account.',
      'Tell us immediately if you suspect unauthorized use.',
    ],
  },
  {
    id: 'appointments',
    heading: 'Appointments and consultations',
    paragraphs: [
      'You can book consultations with doctors and counselors as an in-person visit, a video call or a phone call. The following applies:',
    ],
    bullets: [
      "Times are shown in your own time zone, based on the country in your profile. Each doctor's availability is set in the doctor's own time zone, and we convert it for you.",
      'For video appointments we create a meeting link. For phone appointments the doctor will call the phone number saved in your profile, so please keep it correct and be available at the booked time.',
      'You can cancel an upcoming appointment from the app. Doctors may also cancel or reschedule if something unavoidable comes up. Cancelling frees the time slot for other patients.',
      'Doctors are independent professionals. They, and not Future Family, are responsible for the medical advice they give you.',
      'A booking is not a guarantee of any particular medical outcome.',
    ],
  },
  {
    id: 'health-data',
    heading: 'Health data and connected devices',
    paragraphs: [
      'You may connect wearables or upload records and reports. You confirm you have the right to share that information. Readings from devices can be inaccurate and must not be used to make emergency or treatment decisions on their own.',
      'Doctors you have a confirmed or completed appointment with can view your health information as described in our Privacy Policy.',
    ],
  },
  {
    id: 'your-content',
    heading: 'Content you provide',
    paragraphs: [
      'You keep ownership of the information and files you upload. You give us a limited licence to store, process and display them so that we can provide the service to you and the professionals you choose. You must not upload anything unlawful, or anything that belongs to someone else without permission.',
    ],
  },
  {
    id: 'acceptable-use',
    heading: 'Acceptable use',
    paragraphs: [
      'When using Future Family you agree not to:',
    ],
    bullets: [
      "Break the law or infringe anyone's rights.",
      'Give false information or pretend to be another person, including a doctor.',
      'Harass, threaten or abuse doctors, staff or other users.',
      'Attempt to gain unauthorized access to the service, other accounts or our systems.',
      'Copy, scrape, reverse-engineer or resell any part of the service.',
      'Introduce viruses or anything that could damage or overload the service.',
    ],
  },
  {
    id: 'fees',
    heading: 'Fees and payments',
    paragraphs: [
      'Some features or consultations may be paid. Where a fee applies, the price will be shown before you confirm. Unless the law says otherwise, fees are non-refundable once a consultation has taken place. We will tell you in advance if our pricing changes.',
      'Subscription plans, free trials, renewals and cancellations are covered in the next section.',
    ],
  },
  {
    id: 'subscriptions',
    heading: 'Subscription plans, payments and cancellations',
    paragraphs: [
      'Future Family offers subscription plans that provide access to selected premium features and services. Available plans, pricing, included features and subscription options are described on our Upgrade Profile page.',
      <>
        <Lead>Subscription options and pricing. </Lead>
        Subscription options may include monthly and annual plans. The applicable price, billing frequency and included
        features will be presented to you before you complete your subscription purchase.
      </>,
      <>
        <Lead>Free trial. </Lead>
        If we offer a free trial, the length of the trial, the plan you have chosen and the price that will apply after
        the trial are shown before you start it. Unless you cancel before the trial ends, the plan you picked is charged
        automatically when the trial ends and then renews according to its billing period.
      </>,
      <>
        <Lead>Billing and renewal. </Lead>
        By purchasing a subscription, you authorize the applicable payment provider to charge the displayed subscription
        fee according to the billing terms presented at checkout. If your subscription automatically renews, it will
        continue until canceled in accordance with the applicable cancellation terms.
      </>,
      <>
        <Lead>Subscription cancellation. </Lead>
        You may cancel your subscription using the cancellation method provided by Future Family or the applicable app
        store or payment provider. Cancellation will take effect according to the terms presented at purchase. Any
        continued access to paid features will be subject to the applicable subscription terms.
      </>,
      <>
        <Lead>Changes to plans and pricing. </Lead>
        Future Family may update its subscription plans, features or pricing, subject to applicable law and any required
        notice or consent. Changes to an existing subscription will be communicated where required.
      </>,
      <>
        <Lead>Refunds. </Lead>
        Subscription payments and refund eligibility are subject to applicable law and the refund terms disclosed at
        purchase. Where a subscription is purchased through a third-party platform, its applicable billing and refund
        procedures may also apply.
      </>,
      <>
        <Lead>Access to subscription features. </Lead>
        Access to premium features depends on the active subscription plan, account status and applicable eligibility
        requirements. Future Family may modify or discontinue features in accordance with these Terms and applicable law.
      </>,
    ],
    after: [
      <>
        For current subscription options and pricing, visit our{' '}
        <Link to="/upgrade-profile" style={s.inlineLink}>Upgrade Profile page</Link>.
      </>,
    ],
  },
  {
    id: 'ip',
    heading: 'Our intellectual property',
    paragraphs: [
      'The Future Family name, logo, design, software and content (other than your own content) belong to us or our licensors and are protected by law. You may use them only to use the service as intended.',
    ],
  },
  {
    id: 'availability',
    heading: 'Service availability',
    paragraphs: [
      'We work hard to keep Future Family running, but we do not promise it will always be available, error-free or uninterrupted. We may change, suspend or stop features from time to time, and we may carry out maintenance.',
    ],
  },
  {
    id: 'liability',
    heading: 'Disclaimers and limits of liability',
    paragraphs: [
      'The service is provided "as is" and "as available". To the fullest extent the law allows, we do not give any warranties, express or implied, about the service or the accuracy of health information shown in it.',
      'To the fullest extent the law allows, we are not liable for indirect, incidental or consequential losses, or for the medical advice, acts or omissions of independent doctors. Nothing in these Terms limits liability that cannot be limited by law, such as for death or personal injury caused by negligence, or for fraud.',
    ],
  },
  {
    id: 'termination',
    heading: 'Suspension and termination',
    paragraphs: [
      'You can stop using Future Family and delete your account at any time. We may suspend or close an account if you break these Terms, put others at risk, or if the law requires it. Sections that by their nature should continue, such as those on intellectual property and liability, will continue after your account ends.',
    ],
  },
  {
    id: 'law',
    heading: 'Governing law and disputes',
    paragraphs: [
      'These Terms are governed by the laws of the country in which Future Family is registered, without regard to conflict-of-law rules. Any dispute will be handled by the courts of that country, unless mandatory consumer-protection law where you live gives you other rights.',
    ],
  },
  {
    id: 'changes',
    heading: 'Changes to these terms',
    paragraphs: [
      'We may update these Terms from time to time. If a change is significant we will notify you in the app or by email. If you keep using Future Family after the change takes effect, you accept the updated Terms.',
    ],
  },
  {
    id: 'contact',
    heading: 'Contact us',
    paragraphs: [
      'Questions about these Terms? Email info@futurefamilyai.com or use the Support section inside the app.',
    ],
  },
];

const TermsAndConditions = () => (
  <>
    <Header />
    <div style={s.page}>
      <div style={s.hero}>
        <span style={s.eyebrow}>Please Read</span>
        <h1 style={s.h1}>Terms and Conditions</h1>
        <p style={s.sub}>
          These terms explain what you can expect from Future Family, and what we expect from you.
        </p>

        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadcrumbLink}>Home</Link>
          <span style={{ margin: '0 8px' }}>/</span>
          <span>Terms and Conditions</span>
        </div>
      </div>

      <div style={s.wrap}>
        <div style={s.notice}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Future Family is not an emergency service and does not replace a doctor. In an emergency, call your
            local emergency number.
          </span>
        </div>

        <nav style={s.toc} aria-label="Contents">
          <p style={s.tocTitle}>On this page</p>
          <ol style={{ ...s.tocList, paddingLeft: 0 }}>
            {SECTIONS.map((sec, i) => (
              <li key={sec.id} style={s.tocItem}>
                <a href={`#${sec.id}`} style={s.tocLink}>{i + 1}. {sec.heading}</a>
              </li>
            ))}
          </ol>
        </nav>

        {SECTIONS.map((sec, i) => (
          <Section key={sec.id} index={i + 1} {...sec} />
        ))}

        <p style={s.footer}>&copy; {new Date().getFullYear()} Future Family. All rights reserved.</p>
      </div>
    </div>
    <Footer />
  </>
);

export default TermsAndConditions;